import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import SideMenu from './SideMenu';
import { goalService, userService, subscriptionService } from '../services/api';
import { loadStripe } from '@stripe/stripe-js';
import { useTheme } from '../ThemeContext';
import './Settings.css';

// Import icons from lucide-react
import { User, Palette, Shield, Target, Save, X, Check, AlertCircle, Settings as SettingsIcon } from 'lucide-react';

// Import avatar SVGs
import { ReactComponent as LOTRAvatar } from '../assets/avatars/lotr.svg';
import { ReactComponent as GOTAvatar } from '../assets/avatars/got.svg';
import { ReactComponent as CyberpunkAvatar } from '../assets/avatars/cyberpunk.svg';
import { ReactComponent as GTAAvatar } from '../assets/avatars/gta.svg';
import { ReactComponent as HackerAvatar } from '../assets/avatars/hacker.svg';
import { ReactComponent as StarWarsAvatar } from '../assets/avatars/starwars.svg';
import { ReactComponent as MarvelAvatar } from '../assets/avatars/marvel.svg';
import { ReactComponent as AnimeAvatar } from '../assets/avatars/anime.svg';
import { ReactComponent as PixelAvatar } from '../assets/avatars/pixel.svg';
import { ReactComponent as DisneyAvatar } from '../assets/avatars/disney.svg';

const Settings = () => {
  const location = useLocation();
  const { theme } = useTheme();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [selectedAvatar, setSelectedAvatar] = useState('lotr');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'profile');
  const [saveStatus, setSaveStatus] = useState({ status: '', message: '' });
  const [currentGoal, setCurrentGoal] = useState(null);
  const [newGoalDescription, setNewGoalDescription] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('Beginner');
  const [goalUpdateStatus, setGoalUpdateStatus] = useState({ status: '', message: '' });
  const [educationDegree, setEducationDegree] = useState('');
  
  // --- New profile fields ---
  const [birthDate, setBirthDate] = useState(''); // yyyy-MM-dd
  const [currentStatus, setCurrentStatus] = useState(''); // enum value as string ("0", "1", ...)
  const [motivation, setMotivation] = useState(''); // enum value as string
  
  
  // --- State for Subscription ---
  const [subscriptionStatus, setSubscriptionStatus] = useState({ 
    tier: 'free', 
    isActive: false, 
    endDate: null, 
    loading: true, 
    error: null 
  });
  const [subscriptionActionStatus, setSubscriptionActionStatus] = useState({ status: '', message: '' });
  const [stripePromise, setStripePromise] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  
  const employmentStatusOptions = [
    { value: 0, label: 'Unemployed' },
    { value: 1, label: 'Student' },
    { value: 2, label: 'Full-time employed' },
  ];

  const motivationOptions = [
    { value: 0, label: 'Career growth' },
    { value: 1, label: 'Curiosity' },
    { value: 2, label: 'Change career' },
    { value: 3, label: 'Change life' },
    { value: 4, label: 'Other' },
  ];

  const educationDegreeOptions = [
    { value: '', label: 'Select degree' },
    { value: 'HighSchool', label: 'High school' },
    { value: 'Associate', label: 'Associate' },
    { value: 'Bachelor', label: 'Bachelor' },
    { value: 'Master', label: 'Master' },
    { value: 'PhD', label: 'PhD' },
    { value: 'Other', label: 'Other' },
  ];

  

  const toTwo = (n) => String(n).padStart(2, '0');
  const normalize = (s) => String(s).toLowerCase().replace(/[^a-z0-9]/g, '');
  const nameToEmploymentValue = { unemployed: 0, student: 1, employedfulltime: 2, fulltimeemployed: 2 };
  const nameToMotivationValue = { careergrowth: 0, curiosity: 1, changecareer: 2, changelife: 3, other: 4 };
  const coerceEnum = (val, options, mapping = null) => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'number') return String(val);
    const norm = normalize(val);
    if (mapping && Object.prototype.hasOwnProperty.call(mapping, norm)) {
      return String(mapping[norm]);
    }
    const found = options.find(o => normalize(o.label) === norm || normalize(o.value) === norm);
    return found ? String(found.value) : '';
  };
  
  // Load user data from localStorage
  useEffect(() => {
    // Get saved avatar from localStorage
    const savedAvatar = localStorage.getItem('userAvatar') || 'lotr';
    const savedUsername = localStorage.getItem('username') || 'User';
    setUsername(savedUsername);
    setSelectedAvatar(savedAvatar);
    
    // Fetch user email, current goal and subscription status
    const fetchInitialData = async () => {
      try {
        const userData = await userService.getUser();
        if (userData.goalDescription) {
          if (typeof userData.goalDescription === 'object') {
            setCurrentGoal(userData.goalDescription.description || userData.goalDescription.Description);
          } else {
            setCurrentGoal(userData.goalDescription);
          }
        }
        setEmail(userData.email);
        // Education degree (string enum, handle both cases)
        try {
          const deg = userData.educationDegree ?? userData.EducationDegree ?? '';
          setEducationDegree(deg || '');
        } catch (_) {}

        // Load new profile fields
        if (userData.birthDate) {
          try {
            const dt = new Date(userData.birthDate);
            if (!isNaN(dt.getTime())) {
              const yyyy = dt.getFullYear();
              const mm = toTwo(dt.getMonth() + 1);
              const dd = toTwo(dt.getDate());
              setBirthDate(`${yyyy}-${mm}-${dd}`);
            } else if (typeof userData.birthDate === 'string') {
              setBirthDate(userData.birthDate.substring(0, 10));
            }
          } catch (_) {}
        }
        setCurrentStatus(coerceEnum(userData.currentStatus, employmentStatusOptions, nameToEmploymentValue));
        setMotivation(coerceEnum(userData.motivation, motivationOptions, nameToMotivationValue));
        

        // Fetch Subscription Status
        setSubscriptionStatus(prev => ({ ...prev, loading: true, error: null }));
        const subStatus = await subscriptionService.getStatus();
        setSubscriptionStatus({ 
          tier: subStatus.tier, 
          isActive: subStatus.isActive, 
          endDate: subStatus.endDate, 
          loading: false, 
          error: null 
        });

        // Initialize Stripe 
         if (!stripePromise && subStatus.tier !== 'pro') { 
           // Logic to fetch public key (assuming createCheckoutSession can provide it)
           // const { publicKey } = await subscriptionService.createCheckoutSession();
           // if (publicKey) {
           //    setStripePromise(loadStripe(publicKey));
           // }
         }

      } catch (error) {
        console.error("Error fetching initial data:", error);
        if (error.config?.url?.includes('subscription')) {
          setSubscriptionStatus(prev => ({ ...prev, loading: false, error: 'Failed to load subscription status.' }));
        }
      }
    };
    
    fetchInitialData();
    
    // Listen for sidebar toggle events
    const handleSidebarToggle = (event) => {
      setSidebarCollapsed(event.detail.collapsed);
    };
    
    document.addEventListener('sidebar-toggle', handleSidebarToggle);
    
    return () => {
      document.removeEventListener('sidebar-toggle', handleSidebarToggle);
    };
  }, []);
  
  const handleSaveProfile = async () => {
    localStorage.setItem('username', username);
    setSaveStatus({ status: 'loading', message: 'Saving changes...' });
    try {
      const payload = {
        username,
        birthDate: birthDate || null,
        currentStatus: currentStatus === '' ? null : parseInt(currentStatus),
        motivation: motivation === '' ? null : parseInt(motivation),
        educationDegree: educationDegree || null
      };
      const updated = await userService.updateProfile(payload);
      setSaveStatus({ status: 'success', message: 'Profile updated successfully!' });
      if (updated && updated.username) localStorage.setItem('username', updated.username);
      setTimeout(() => setSaveStatus({ status: '', message: '' }), 2500);
    } catch (e) {
      console.error('Failed to update profile', e);
      setSaveStatus({ status: 'error', message: e.response?.data?.message || 'Failed to save changes' });
      setTimeout(() => setSaveStatus({ status: '', message: '' }), 4000);
    }
  };

  const handleSaveAppearance = () => {
    // Save the selected avatar to localStorage
    localStorage.setItem('userAvatar', selectedAvatar);
    
    // Simulate API call
    setSaveStatus({ status: 'loading', message: 'Saving changes...' });
    
    setTimeout(() => {
      setSaveStatus({ status: 'success', message: 'Appearance updated successfully!' });
      
      // Notify other components that the avatar has changed
      document.dispatchEvent(new CustomEvent('avatar-updated', { 
        detail: { avatar: selectedAvatar } 
      }));
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSaveStatus({ status: '', message: '' });
      }, 3000);
    }, 1000);
  };
  
  // --- Handlers for Subscription ---
  const handleGetPro = async () => {
    setSubscriptionActionStatus({ status: 'loading', message: 'Redirecting to checkout...' });
    try {
      const { sessionId, publicKey } = await subscriptionService.createCheckoutSession();
      
      // Initialize Stripe *here* with the fetched public key
      const stripe = await loadStripe(publicKey); 
      if (!stripe) {
        throw new Error("Stripe.js failed to load.");
      }

      const { error } = await stripe.redirectToCheckout({ sessionId });

      if (error) {
        console.error("Stripe checkout error:", error);
        setSubscriptionActionStatus({ status: 'error', message: error.message || 'Failed to redirect to checkout.' });
      }
      // If redirection is successful, the user leaves this page. 
      // No need to clear loading status here.
    } catch (error) {
      console.error("Error creating checkout session:", error);
      setSubscriptionActionStatus({ 
        status: 'error', 
        message: error.response?.data?.message || 'Failed to initiate upgrade.' 
      });
       // Clear error message after 5 seconds
      setTimeout(() => {
        setSubscriptionActionStatus({ status: '', message: '' });
      }, 5000);
    }
  };

  const handleCancelSubscriptionConfirmed = async () => {
    setSubscriptionActionStatus({ status: 'loading', message: 'Canceling subscription...' });
    try {
      const response = await subscriptionService.cancelSubscription();
      setSubscriptionActionStatus({ status: 'success', message: response.message || 'Subscription cancellation initiated.' });
      setShowCancelModal(false);
      // refresh status later
      setTimeout(async () => {
        try {
          const subStatus = await subscriptionService.getStatus();
          setSubscriptionStatus({ 
            tier: subStatus.tier, 
            isActive: subStatus.isActive, 
            endDate: subStatus.endDate, 
            loading: false, 
            error: null 
          });
          setSubscriptionActionStatus({ status: '', message: '' });
        } catch (refreshError) {
          console.error("Error refreshing subscription status:", refreshError);
        }
      }, 3000);
    } catch (error) {
      console.error("Error canceling subscription:", error);
      setSubscriptionActionStatus({ 
        status: 'error', 
        message: error.response?.data?.message || 'Failed to cancel subscription.' 
      });
      setTimeout(() => {
        setSubscriptionActionStatus({ status: '', message: '' });
      }, 5000);
    }
  };

  const openCancelModal = () => setShowCancelModal(true);
  const closeCancelModal = () => setShowCancelModal(false);
  
  const avatars = [
    { id: 'lotr', component: LOTRAvatar, name: 'Lord of the Rings' },
    { id: 'got', component: GOTAvatar, name: 'Game of Thrones' },
    { id: 'cyberpunk', component: CyberpunkAvatar, name: 'Cyberpunk' },
    { id: 'gta', component: GTAAvatar, name: 'GTA' },
    { id: 'hacker', component: HackerAvatar, name: 'Hacker' },
    { id: 'starwars', component: StarWarsAvatar, name: 'Star Wars' },
    { id: 'marvel', component: MarvelAvatar, name: 'Marvel' },
    { id: 'anime', component: AnimeAvatar, name: 'Anime' },
    { id: 'pixel', component: PixelAvatar, name: 'Pixel Art' },
    { id: 'disney', component: DisneyAvatar, name: 'Disney' },
  ];
  
  return (
    <div className="app-layout">
      <SideMenu/>
      
      <div className={`learning-plan-main-content ${theme}`}>
        <div className={`settings-container ${theme}`}>
          <div className="settings-header">
            <div className="panel-title">
              <SettingsIcon size={24} />
              <h2>Settings</h2>
            </div>
          </div>
          
          <div className="settings-tabs">
            <button 
              className={`tab-button ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              <User size={16} />
              Profile
            </button>
            <button 
              className={`tab-button ${activeTab === 'appearance' ? 'active' : ''}`}
              onClick={() => setActiveTab('appearance')}
            >
              <Palette size={16} />
              Avatar
            </button>
            <button 
              className={`tab-button ${activeTab === 'privacy' ? 'active' : ''}`}
              onClick={() => setActiveTab('privacy')}
            >
              <Shield size={16} />
              Privacy
            </button>
            <button 
              className={`tab-button ${activeTab === 'subscription' ? 'active' : ''}`}
              onClick={() => setActiveTab('subscription')}
            >
              Subscription
            </button>
          </div>
          
          <div className={`settings-content ${theme}`}>
            {activeTab === 'profile' && (
              <div className="profile-settings">
                <h3 style={{textAlign: 'left'}}>Profile Settings</h3>
                
                <div className="form-group">
                  <label htmlFor="username">Username</label>
                  <input
                    type="text"
                    id="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter your username"
                  />
                </div>
                
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={email}
                    disabled
                    className="disabled-input"
                  />
                  <small>Email cannot be changed</small>
                </div>

                {/* New profile fields */}
                <div className="form-group">
                  <label htmlFor="birthDate">Birth date</label>
                  <input
                    id="birthDate"
                    type="date"
                    value={birthDate}
                    onChange={(e) => setBirthDate(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="educationDegree">Education degree</label>
                  <select
                    id="educationDegree"
                    className="skill-level-dropdown"
                    value={educationDegree || ''}
                    onChange={(e) => setEducationDegree(e.target.value)}
                  >
                    {educationDegreeOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="currentStatus">Current status</label>
                  <select
                    id="currentStatus"
                    className="skill-level-dropdown"
                    value={currentStatus}
                    onChange={(e) => setCurrentStatus(e.target.value)}
                  >
                    <option value="">Select status</option>
                    {employmentStatusOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="motivation">Motivation</label>
                  <select
                    id="motivation"
                    className="skill-level-dropdown"
                    value={motivation}
                    onChange={(e) => setMotivation(e.target.value)}
                  >
                    <option value="">Select motivation</option>
                    {motivationOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                
                
                <button 
                  className="save-button modern-button primary-button"
                  onClick={handleSaveProfile}
                  disabled={saveStatus.status === 'loading'}
                >
                  {saveStatus.status === 'loading' ? (
                    <>
                      <div className="button-spinner"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      Save Changes
                    </>
                  )}
                </button>
                
                {saveStatus.message && (
                  <div className={`save-status ${saveStatus.status}`}>
                    {saveStatus.status === 'success' && <Check size={16} />}
                    {saveStatus.status === 'error' && <AlertCircle size={16} />}
                    <span>{saveStatus.message}</span>
                  </div>
                )}
              </div>
            )}
            
            {activeTab === 'appearance' && (
              <div className="appearance-settings">
                <h3 style={{textAlign: 'left'}}>Select Avatar</h3>
                
                <div className="avatar-selection">
                  <div className="avatars-grid">
                    {avatars.map(avatar => (
                      <div 
                        key={avatar.id}
                        className={`avatar-option ${selectedAvatar === avatar.id ? 'selected' : ''}`}
                        onClick={() => setSelectedAvatar(avatar.id)}
                      >
                        <div className="avatar-preview">
                          <avatar.component />
                        </div>
                        <span>{avatar.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <button 
                  className="save-button modern-button primary-button"
                  onClick={handleSaveAppearance}
                  disabled={saveStatus.status === 'loading'}
                >
                  {saveStatus.status === 'loading' ? (
                    <>
                      <div className="button-spinner"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      Save Changes
                    </>
                  )}
                </button>
                
                {saveStatus.message && (
                  <div className={`save-status ${saveStatus.status}`}>
                    {saveStatus.status === 'success' && <Check size={16} />}
                    {saveStatus.status === 'error' && <AlertCircle size={16} />}
                    <span>{saveStatus.message}</span>
                  </div>
                )}
              </div>
            )}
            
            {activeTab === 'goals' && (
              <div className="goals-settings">
                <h3>Learning Goal Settings</h3>
                
                <div className="current-goal-section">
                  <h4>Current Goal</h4>
                  {currentGoal ? (
                    <div className="goal-info">
                      <p>{typeof currentGoal === 'object' ? JSON.stringify(currentGoal) : currentGoal}</p>
                      <div className="goal-actions">
                        <button 
                          className="change-goal-button modern-button secondary-button"
                          onClick={() => {
                            setNewGoalDescription('');
                            setSelectedLevel('Beginner');
                            document.getElementById('newGoalModal').style.display = 'flex';
                          }}
                        >
                          <Target size={16} />
                          Change Goal
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p>No current goal set</p>
                      <button 
                        className="set-goal-button modern-button primary-button"
                        onClick={() => {
                          setNewGoalDescription('');
                          setSelectedLevel('Beginner');
                          document.getElementById('newGoalModal').style.display = 'flex';
                        }}
                      >
                        <Target size={16} />
                        Set Goal
                      </button>
                    </div>
                  )}
                </div>
                
                <div id="newGoalModal" className="modal">
                  <div className={`modal-content ${theme}`}>
                    <button 
                      className="modal-close-button"
                      onClick={() => {
                        document.getElementById('newGoalModal').style.display = 'none';
                      }}
                    >
                      <X size={20} />
                    </button>
                    <h3>{currentGoal ? 'Change Goal' : 'Set New Goal'}</h3>
                    <div className="form-group">
                      <label htmlFor="newGoal">Goal Description</label>
                      <textarea
                        id="newGoal"
                        value={newGoalDescription}
                        onChange={(e) => setNewGoalDescription(e.target.value)}
                        placeholder="Describe your learning goal..."
                        rows={3}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="skillLevel">Course Difficulty</label>
                      <select 
                        id="skillLevel" 
                        value={selectedLevel}
                        onChange={(e) => setSelectedLevel(e.target.value)}
                        className="skill-level-dropdown"
                      >
                        <option value="Beginner">Beginner</option>
                        <option value="Practitioner">Practitioner</option>
                        <option value="Advanced">Advanced</option>
                      </select>
                    </div>
                    
                    <button 
                      className="save-button modern-button primary-button"
                      onClick={async () => {
                        if (!newGoalDescription.trim()) {
                          setGoalUpdateStatus({ 
                            status: 'error', 
                            message: 'Please enter a goal description' 
                          });
                          return;
                        }
                        
                        try {
                          setGoalUpdateStatus({ 
                            status: 'loading', 
                            message: currentGoal ? 'Changing goal...' : 'Creating new goal...' 
                          });
                          
                          const fullGoalDescription = `${newGoalDescription} (${selectedLevel})`;
                          
                          let goal;
                          if (currentGoal) {
                            goal = await goalService.updateGoal(fullGoalDescription);
                          } else {
                            goal = await goalService.createGoal(fullGoalDescription);
                          }
                          const goalText = typeof goal === 'object' ? (goal.description || goal.Description || JSON.stringify(goal)) : goal;
                          setCurrentGoal(goalText);
                          setNewGoalDescription('');
                          
                          document.dispatchEvent(new CustomEvent('goal-updated', { 
                            detail: { goal: goalText }
                          }));
                          
                          setGoalUpdateStatus({ 
                            status: 'success', 
                            message: currentGoal ? 'Goal changed successfully!' : 'New goal created successfully!' 
                          });
                          document.getElementById('newGoalModal').style.display = 'none';
                        } catch (error) {
                          console.error("Goal Update Error:", error);
                          setGoalUpdateStatus({ 
                            status: 'error', 
                            message: error.response?.data?.message || (currentGoal ? 'Failed to change goal' : 'Failed to create new goal')
                          });
                        }
                      }}
                      disabled={goalUpdateStatus.status === 'loading'}
                    >
                      {goalUpdateStatus.status === 'loading' ? (
                        <>
                          <div className="button-spinner"></div>
                          {currentGoal ? 'Changing...' : 'Creating...'}
                        </>
                      ) : (
                        <>
                          <Target size={16} />
                          {currentGoal ? 'Change Goal' : 'Create Goal'}
                        </>
                      )}
                    </button>
                    
                    {goalUpdateStatus.message && goalUpdateStatus.status !== 'success' && (
                      <div className={`save-status ${goalUpdateStatus.status}`} style={{marginTop: '15px'}}>
                        {goalUpdateStatus.status === 'error' && <AlertCircle size={16} />}
                        <span>{goalUpdateStatus.message}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {goalUpdateStatus.message && goalUpdateStatus.status === 'success' && (
                  <div className={`save-status ${goalUpdateStatus.status}`} style={{marginTop: '15px', textAlign: 'center'}}>
                    <Check size={16} />
                    <span>{goalUpdateStatus.message}</span>
                  </div>
                )}
              </div>
            )}
            
            {activeTab === 'privacy' && (
              <div className="privacy-settings">
                <h3>Privacy Policy</h3>
                
                <div className="policy-content">
                  <h4>Brainy AI Privacy Policy</h4>
                  <p>Last updated: May 2025</p>
                  
                  <h5>1. Introduction and Data Controller</h5>
                  <p>
                    Welcome to Brainy AI. We are committed to protecting your privacy and personal data in accordance with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
                  </p>
                  <p>
                    This privacy policy explains how we collect, use, store, and protect your personal data when you use our educational AI platform. It also informs you of your rights under UK data protection law.
                  </p>
                  <p>
                    <strong>Contact:</strong> brainyai.info@gmail.com
                    <br/>
                  </p>
                  
                  <h5>2. Legal Basis for Processing</h5>
                  <p>We process your personal data under the following legal bases:</p>
                  <ul>
                    <li><strong>Contract Performance:</strong> To provide our educational services as agreed in our Terms of Service</li>
                    <li><strong>Legitimate Interest:</strong> To improve our services, conduct analytics, and ensure platform security</li>
                    <li><strong>Consent:</strong> For marketing communications and optional features (where explicitly requested)</li>
                    <li><strong>Legal Obligation:</strong> To comply with applicable laws and regulations</li>
                  </ul>
                  
                  <h5>3. Personal Data We Collect</h5>
                  <p>We collect and process the following categories of personal data:</p>
                  <ul>
                    <li><strong>Account Information:</strong> Name, email address, username, password (encrypted)</li>
                    <li><strong>Learning Data:</strong> Educational goals, progress tracking, course preferences, skill assessments</li>
                    <li><strong>Communication Data:</strong> Chat conversations with our AI assistant, support inquiries</li>
                    <li><strong>Technical Data:</strong> IP address, browser type, device information, usage analytics</li>
                    <li><strong>Payment Data:</strong> Billing information (processed securely through third-party payment processors)</li>
                  </ul>
                  
                  <h5>4. How We Use Your Data</h5>
                  <p>We use your personal data for the following purposes:</p>
                  <ul>
                    <li>Providing and maintaining our educational AI services</li>
                    <li>Personalizing your learning experience and recommendations</li>
                    <li>Processing payments and managing subscriptions</li>
                    <li>Communicating with you about your account and our services</li>
                    <li>Improving our platform through analytics and user feedback</li>
                    <li>Ensuring platform security and preventing fraud</li>
                    <li>Complying with legal obligations and resolving disputes</li>
                  </ul>
                  
                  <h5>5. Data Sharing and Third Parties</h5>
                  <p>We may share your data with:</p>
                  <ul>
                    <li><strong>Service Providers:</strong> Cloud hosting, payment processing, analytics (under strict data processing agreements)</li>
                    <li><strong>Legal Requirements:</strong> When required by law, court order, or to protect our rights</li>
                    <li><strong>Business Transfers:</strong> In case of merger, acquisition, or sale of assets (with appropriate safeguards)</li>
                  </ul>
                  <p>We do not sell your personal data to third parties for marketing purposes.</p>
                  
                  <h5>6. International Data Transfers</h5>
                  <p>
                    Your data may be processed outside the UK. When this occurs, we ensure adequate protection through:
                  </p>
                  <ul>
                    <li>Adequacy decisions by the UK government</li>
                    <li>Standard Contractual Clauses approved by UK authorities</li>
                    <li>Other appropriate safeguards as required by UK GDPR</li>
                  </ul>
                  
                  <h5>7. Data Retention</h5>
                  <p>We retain your personal data only as long as necessary for the purposes outlined in this policy:</p>
                  <ul>
                    <li><strong>Account Data:</strong> Until account deletion or 3 years after last activity</li>
                    <li><strong>Learning Progress:</strong> Until account deletion or as required for service provision</li>
                    <li><strong>Communication Records:</strong> 6 years for legal compliance</li>
                    <li><strong>Technical Logs:</strong> 12 months for security and analytics</li>
                  </ul>
                  
                  <h5>8. Your Rights Under UK GDPR</h5>
                  <p>You have the following rights regarding your personal data:</p>
                  <ul>
                    <li><strong>Right of Access:</strong> Request copies of your personal data</li>
                    <li><strong>Right to Rectification:</strong> Correct inaccurate or incomplete data</li>
                    <li><strong>Right to Erasure:</strong> Request deletion of your data (subject to legal obligations)</li>
                    <li><strong>Right to Restrict Processing:</strong> Limit how we use your data</li>
                    <li><strong>Right to Data Portability:</strong> Receive your data in a structured format</li>
                    <li><strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
                    <li><strong>Right to Withdraw Consent:</strong> Where processing is based on consent</li>
                  </ul>
                  <p>To exercise these rights, contact us at brainyai.info@gmail.com. We will respond within one month.</p>
                  
                  <h5>9. Data Security</h5>
                  <p>
                    We implement appropriate technical and organizational measures to protect your personal data, including:
                  </p>
                  <ul>
                    <li>Encryption of data in transit and at rest</li>
                    <li>Regular security assessments and updates</li>
                    <li>Access controls and staff training</li>
                    <li>Incident response procedures</li>
                  </ul>
                  
                  <h5>10. Cookies and Tracking</h5>
                  <p>
                    We use essential cookies for platform functionality and analytics cookies to improve our services. 
                    You can manage cookie preferences through your browser settings. For detailed information, 
                    see our Cookie Policy.
                  </p>
                  
                  <h5>11. Children's Privacy</h5>
                  <p>
                    Our services are not intended for children under 13. We do not knowingly collect personal data 
                    from children under 13. If you believe we have collected such data, please contact us immediately.
                  </p>
                  
                  <h5>12. Changes to This Policy</h5>
                  <p>
                    We may update this privacy policy periodically. We will notify you of significant changes 
                    via email or platform notification. Continued use of our services constitutes acceptance 
                    of the updated policy.
                  </p>
                  
                  <h5>13. Contact and Complaints</h5>
                  <p>
                    For privacy-related questions or to exercise your rights, contact us at:
                  </p>
                  <ul>
                    <li><strong>Email:</strong> brainyai.info@gmail.com</li>
                  </ul>
                  <p>
                    If you are not satisfied with our response, you have the right to lodge a complaint 
                    with the Information Commissioner's Office (ICO) at ico.org.uk or by calling 0303 123 1113.
                  </p>
                </div>
              </div>
            )}
            
            {activeTab === 'subscription' && (
              <div className="subscription-settings">
                <h3 style={{textAlign: 'left'}}>Subscription Settings</h3>
                
                {subscriptionStatus.loading && <p>Loading subscription status...</p>}
                {subscriptionStatus.error && <p className="error-message">{subscriptionStatus.error}</p>}
                
                {!subscriptionStatus.loading && !subscriptionStatus.error && (
                  <div className="subscription-details">
                    <h4>Current Plan: <span className={`tier-${subscriptionStatus.tier}`}>{subscriptionStatus.tier.toUpperCase()}</span></h4>
                    
                    {/* Pro Plan Features Section */}
                    <div className="pro-features-section">
                      <h4>Pro Plan Features</h4>
                      <div className="features-comparison">
                        <div className="plan-features">
                          <div className="plan-column">
                            <h5>Free Plan</h5>
                            <ul className="features-list">
                              <li><Check size={16} /> Personalized Learning Roadmap</li>
                              <li><Check size={16} /> 1 Educational goal</li>
                              <li><Check size={16} /> 1 Topic per goal</li>
                              <li><Check size={16} /> Notes management</li>
                              <li><Check size={16} /> 5 AI Chat Queries/day</li>
                              <li><Check size={16} /> Pomodoro Timer</li>
                            </ul>
                          </div>
                          <div className="plan-column pro-column">
                            <h5>Pro Plan (£5/month)</h5>
                            <ul className="features-list">
                              <li><Check size={16} /> Everything in Free, plus:</li>
                              <li><Check size={16} /> Up to 10 goals in progress</li>
                              <li><Check size={16} /> Unlimited AI Chat Queries</li>
                              <li><Check size={16} /> Unlimited AI Lessons</li>
                              <li><Check size={16} /> Unlimited AI Assessments</li>
                              <li><Check size={16} /> Best AI Models</li>
                            </ul>
                          </div>
                        </div>
                    </div>
                    </div>
                    
                    {subscriptionStatus.tier === 'free' && (
                      <div className="upgrade-section">
                        <p>Upgrade to Pro for advanced features!</p>
                        <button 
                          className="upgrade-button"
                          onClick={handleGetPro}
                          disabled={subscriptionActionStatus.status === 'loading'}
                        >
                          {subscriptionActionStatus.status === 'loading' ? 'Redirecting...' : 'Get Pro Plan'}
                        </button>
                      </div>
                    )}
                    
                    {subscriptionStatus.tier === 'pro' && subscriptionStatus.isActive && (
                      <div className="pro-details">
                        <p>
                          Your subscription is active.
                          {subscriptionStatus.endDate && 
                            ` It is valid until: ${new Date(subscriptionStatus.endDate).toLocaleDateString()}`
                          }
                        </p>
                         <button 
                          className="cancel-button"
                          onClick={openCancelModal}
                          disabled={subscriptionActionStatus.status === 'loading'}
                        >
                          {subscriptionActionStatus.status === 'loading' ? 'Canceling...' : 'Cancel Subscription'}
                        </button>
                         <small>
                           If you cancel, your Pro access will continue until the end of the current billing period.
                         </small>
                      </div>
                    )}

                     {subscriptionStatus.tier === 'pro' && !subscriptionStatus.isActive && subscriptionStatus.endDate && (
                       <div className="pro-details canceled">
                         <p>
                           Your Pro subscription has been canceled and will expire on: 
                           {new Date(subscriptionStatus.endDate).toLocaleDateString()}.
                         </p>
                       </div>
                     )}
                  </div>
                )}

                {subscriptionActionStatus.message && (
                  <div className={`action-status ${subscriptionActionStatus.status}`}>
                    {subscriptionActionStatus.message}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <div className="modal" id="cancelSubModal" style={{display:'flex'}}>
          <div className={`modal-content ${theme}`} style={{maxWidth:'500px'}}>
            <h3 style={{marginBottom:'10px'}}>Confirm Cancellation</h3>
                      <p>You will lose access to the following Pro features at the end of your billing period:</p>
          <ul style={{textAlign:'left', marginLeft:'20px', lineHeight:'1.5'}}>
            <li>Up to 10 goals in progress</li>
            <li>Unlimited AI Chat Queries</li>
            <li>Unlimited AI Lessons</li>
            <li>Unlimited AI Assessments</li>
            <li>Best AI Models</li>
          </ul>
            <div style={{display:'flex', gap:'12px', marginTop:'20px', justifyContent:'flex-end'}}>
              <button className="secondary-button modern-button" onClick={closeCancelModal}>Keep Pro</button>
              <button className="danger-button modern-button" onClick={handleCancelSubscriptionConfirmed}>Confirm Cancellation</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings; 